import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahCompanyExternalBudgets } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

/**
 * One budget the import wrote, in either the created or the updated list.
 *
 * `id` is deliberately the same value as `sah_budget_id`: TableView compares selection as
 * `String(row.id)`, so the deferred "tick which ones to apply" step can reuse these exact rows and
 * columns without a new table component.
 *
 * `start_date` / `end_date` arrive as ISO datetime strings (they come straight off the written row),
 * NOT as bare calendar dates, so anything displaying them must format them - see
 * forms/sah-budgets/importResults.ts.
 */
export interface ImportBudgetItem {
  id: string;
  sah_budget_id: string;
  client_id: number;
  client_name: string;
  // The resolved TurnPoint funding type name for the written row.
  budget_type: string;
  start_date: string | null;
  end_date: string | null;
}

/**
 * A budget the import fetched but did not write, with the first reason it could not be written.
 *
 * There is deliberately no `client_id`: a skipped budget usually has no matching TurnPoint client,
 * which is the most common reason for skipping in the first place. `client_name` is null whenever
 * nothing matched. Unlike an item, `budget_type` here is the RAW Services Australia code, because
 * there may be no TurnPoint funding type to resolve it against.
 */
export interface ImportBudgetSkipped {
  care_recipient_id: string | null;
  sah_budget_id: string | null;
  client_name: string | null;
  budget_type: string | null;
  reason: string;
}

/**
 * The `data` payload of `POST .../companies/external/budgets/import`, after `unwrap()`.
 *
 * `window` carries BARE calendar dates (YYYY-MM-DD) and is the authoritative record of what the
 * server actually asked Services Australia for. `truncated` means the server hit its defensive page
 * cap, not that anything failed - the run is real, it just did not reach the end of the list.
 */
export interface ImportBudgetsResponse {
  window: { effective_date_from: string; effective_date_to: string };
  fetched: number;
  truncated: boolean;
  created: number;
  updated: number;
  created_items: ImportBudgetItem[];
  updated_items: ImportBudgetItem[];
  skipped: ImportBudgetSkipped[];
}

/**
 * Company-scoped Support at Home budget actions (TAV-3168).
 *
 * A slice of its own rather than an endpoint on acoBudgetsApi: that slice's baseUrl is
 * `/v2/sah/clients/external/budgets`, and this route lives under `/v2/sah/companies/...`. Reaching
 * it from there would need an absolute or `../..` url, which would make that slice's baseUrl a lie.
 *
 * This slice MUST stay registered in `supportAtHomeApis` (reducers/index.ts) - store.ts reads that
 * array for both reducers and middleware, and without it the hook silently no-ops.
 */
export const sahCompanyBudgetsApi = createApi({
  reducerPath: 'sahCompanyBudgetsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahCompanyExternalBudgets }),
  tagTypes: ['sah-company-budgets'],
  endpoints: (builder) => ({
    importBudgets: builder.mutation<ImportBudgetsResponse, void>({
      query: () => ({ url: '/import', method: 'POST', body: {} }),
      // No invalidatesTags. The SaH budgets list is a classic AsyncThunk slice, so no RTK Query tag
      // can reach it (and tags do not cross createApi slices anyway). The modal's onClose calls the
      // parent's load() instead, exactly as the ACO import modal already does.
      transformResponse: (payload) => unwrap<ImportBudgetsResponse>(payload),
    }),
  }),
});

export const { useImportBudgetsMutation } = sahCompanyBudgetsApi;
